import React, { useCallback, useState, useEffect, useRef, useMemo, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useWindowScroll } from 'react-use';
import PropTypes from 'prop-types';
import { reject, propEq } from 'ramda';
import { Box, Chip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { isFunction } from '@alycecom/utils';
import {
  AutocompleteMultiSelectFilter,
  FilterChips,
  MultiSelectFilter,
  SearchFilter,
  SortingSelect,
  ProductSidebar as Sidebar,
  ProductSidebarHeader as SidebarHeader,
  ProductSidebarTitle as SidebarTitle,
} from '@alycecom/ui';

import {
  GIFT_TYPES_FILTER,
  CATEGORIES_FILTER,
  BRANDS_FILTER,
  SEARCH_FILTER_OPTIONS,
} from '../../constants/filters.constants';
import {
  getFilterChips,
  getFilters,
  getPagination,
  getSearch,
  getSorting,
  getIsLoading,
  getIsSearchAvailable,
  getCountryId,
} from '../../store/products/products.selectors';
import {
  setSearch,
  setSorting,
  resetFilters,
  setFilter,
  addChip,
  deleteChip,
  clearAllChips,
  setAllFilters,
  setChips,
  setCountryId,
} from '../../store/products/products.actions';
import {
  loadGiftTypesRequest,
  loadCategoriesRequest,
  loadBrandsRequest,
  resetFilters as resetFiltersData,
} from '../../store/filters/filters.actions';
import useMarketplaceFilter from '../../hooks/useMarketplaceFilter';
import MobileFilterButton from '../MobileFilters/MobileFilterButton';
import { CountryShape } from '../../../../shapes';
import useResponsive from '../../../../hooks/useResponsive';
import MobileFilters from '../MobileFilters/MobileFilters';
import { getKeysCount } from '../../../../helpers/list.helpers';
import useTrackRecipient from '../../../../hooks/useTrackRecipient';
import { getSortingItems } from '../../constants/sorting.constants';
import CountriesFilter from '../CountriesFilter/CountriesFilter';
import { getWhiteLabeling } from '../../../RecipientModule/store/statuses/statuses.selectors';

import { useStyles } from './marketplaceFilters.styles';

const STICKY_POSITION = 230;
const MOBILE_STICKY_POSITION = 150;

const MarketplaceFilters = ({
  isDonation,
  isDeclaredDataEnabled,
  hasCustomMarketplace,
  countries,
  onSticky,
  onChangeCountry,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { isMobile } = useResponsive();
  const { palette } = useTheme();
  const trackEvent = useTrackRecipient();

  const filterRef = useRef(null);
  const componentJustMounted = useRef(true);

  const isLoading = useSelector(getIsLoading);
  const filters = useSelector(getFilters);
  const search = useSelector(getSearch);
  const isSearchEnabled = useSelector(getIsSearchAvailable);
  const sorting = useSelector(getSorting);
  const pagination = useSelector(getPagination);
  const filterChips = useSelector(getFilterChips);
  const countryId = useSelector(getCountryId);
  const isWhiteLabeling = useSelector(getWhiteLabeling);

  const stickyProps = useMemo(
    () => ({
      zIndex: 1200,
      position: 'fixed',
      top: 0,
      bgcolor: alpha(palette.common.white, 0.95),
      mt: 0,
      boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.08)',
    }),
    [palette],
  );

  const sortingItems = useMemo(
    () => getSortingItems({ isDeclaredDataEnabled, hasCustomMarketplace, isWhiteLabeling }),
    [isDeclaredDataEnabled, hasCustomMarketplace, isWhiteLabeling],
  );

  const handleOnChangeFilter = useCallback(
    (name, option, checked) => {
      const chipAction = checked ? addChip : deleteChip;
      dispatch(chipAction({ name, ...option }));
    },
    [dispatch],
  );

  const handleCloseSearch = useCallback(
    value => {
      dispatch(deleteChip({ ...SEARCH_FILTER_OPTIONS }));
      if (value) {
        dispatch(addChip({ ...SEARCH_FILTER_OPTIONS, label: value }));
      }
    },
    [dispatch],
  );

  const giftTypesFilter = useMarketplaceFilter(GIFT_TYPES_FILTER, loadGiftTypesRequest, handleOnChangeFilter);
  const categoriesFilter = useMarketplaceFilter(CATEGORIES_FILTER, loadCategoriesRequest, handleOnChangeFilter);
  const brandsFilter = useMarketplaceFilter(BRANDS_FILTER, loadBrandsRequest, handleOnChangeFilter);

  const handleSearch = useCallback(value => dispatch(setSearch(value)), [dispatch]);
  const handleSort = useCallback(value => dispatch(setSorting(value)), [dispatch]);

  // Mobile filter flow
  const [isFiltersSidebarOpen, setIsFiltersSidebarOpen] = useState(false);
  const handleOpenFilters = useCallback(() => {
    setIsFiltersSidebarOpen(true);
    trackEvent('Marketplace Filters - Opened');
  }, [trackEvent]);
  const handleCloseFilters = useCallback(() => {
    setIsFiltersSidebarOpen(false);
    trackEvent('Marketplace Filters - Closed');
  }, [trackEvent]);
  const handleApplyFilters = useCallback(
    state => {
      const { sort, search: mobileSearch, filters: mobileFilters, filterChips: chips } = state;
      dispatch(setSearch(mobileSearch));
      dispatch(setSorting(sort));
      dispatch(setAllFilters(mobileFilters));
      dispatch(setChips(chips));
      setIsFiltersSidebarOpen(false);
    },
    [dispatch],
  );

  // Removing chips
  const handleDeleteChip = useCallback(
    value => {
      const { id, name } = value;
      if (name === SEARCH_FILTER_OPTIONS.name) {
        dispatch(setSearch(''));
      } else {
        const selectedValues = filters[name];
        const values = reject(propEq('id', id), selectedValues);
        dispatch(setFilter({ key: name, items: values }));
      }
      dispatch(deleteChip({ id, name }));
    },
    [dispatch, filters],
  );

  const handleClearAllChips = useCallback(() => {
    dispatch(clearAllChips());
    dispatch(setSearch(''));
    dispatch(resetFilters());
  }, [dispatch]);

  const handleChangeCountry = useCallback(
    country => {
      const selectedCountryId = parseInt(country?.id, 10);
      dispatch(resetFilters());
      dispatch(resetFiltersData());
      dispatch(setCountryId(selectedCountryId));
      if (isFunction(onChangeCountry)) {
        onChangeCountry(selectedCountryId);
      }
    },
    [dispatch, onChangeCountry],
  );

  // Sticky and scrolling options
  const [filterBoxProps, setFilterBoxProps] = useState({});
  const { y } = useWindowScroll();
  const stickyPosition = isMobile ? MOBILE_STICKY_POSITION : STICKY_POSITION;
  const [isScrolling, setIsScrolling] = useState(false);

  const sidebarHeader = (
    <SidebarHeader overrideClasses={classes} onClose={handleCloseFilters}>
      <SidebarTitle title="All Filters" />
    </SidebarHeader>
  );

  const chips = useMemo(
    () =>
      filterChips.map(chip => (
        <Chip onDelete={() => handleDeleteChip(chip)} key={chip.id} label={chip.label} size="small" />
      )),
    [filterChips, handleDeleteChip],
  );

  useEffect(() => {
    const { current: mount } = componentJustMounted;
    if (pagination.currentPage === 1 && isLoading && y > stickyPosition && !isScrolling) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: mount ? 'auto' : 'smooth',
      });
      setIsScrolling(true);
    }
    if (y === 0) {
      setIsScrolling(false);
    }
  }, [isLoading, pagination, y, isScrolling, stickyPosition, componentJustMounted]);

  useEffect(() => {
    const { current: mount } = componentJustMounted;
    const hasSticky = getKeysCount(filterBoxProps) > 0;

    if (y > stickyPosition && !hasSticky && !mount) {
      setFilterBoxProps(stickyProps);
      onSticky(filterRef, true);
    }

    if (y < stickyPosition && hasSticky) {
      setFilterBoxProps({});
      onSticky(filterRef, false);
    }

    if (mount) {
      componentJustMounted.current = false;
    }
  }, [y, filterBoxProps, stickyPosition, componentJustMounted, filterRef, stickyProps, onSticky]);

  return (
    <Box
      ref={filterRef}
      width={1}
      my={2}
      px={[2, 4, 5]}
      py={1}
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      justifyContent="flex-start"
      color="text.main"
      {...filterBoxProps}
    >
      <Box
        width={1}
        alignItems="center"
        justifyContent="space-between"
        mb={1}
        sx={{ display: { lg: 'none', sm: 'none', xs: 'flex' } }}
      >
        <CountriesFilter width={1} mr={2} countryId={countryId} countries={countries} onChange={handleChangeCountry} />
      </Box>
      <Box width={1} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box sx={{ display: { lg: 'block', sm: 'block', xs: 'none' } }}>
          <CountriesFilter countryId={countryId} countries={countries} onChange={handleChangeCountry} />
        </Box>
        <Box display="flex" justifyContent="center">
          <Box sx={{ display: { lg: 'none', sm: 'flex', xs: 'flex' } }}>
            <MobileFilterButton onClick={handleOpenFilters} />
            {isSearchEnabled && <SearchFilter value={search} onSearch={handleSearch} onClose={handleCloseSearch} />}
          </Box>
          <Box sx={{ display: { lg: 'flex', sm: 'none', xs: 'none' } }}>
            {isSearchEnabled && <SearchFilter value={search} onSearch={handleSearch} onClose={handleCloseSearch} />}
            {!isDonation && (
              <MultiSelectFilter
                name={GIFT_TYPES_FILTER.key}
                label={GIFT_TYPES_FILTER.label}
                value={filters[GIFT_TYPES_FILTER.key]}
                options={giftTypesFilter.options}
                isLoading={giftTypesFilter.isLoading}
                onOpen={giftTypesFilter.openFilter}
                onChange={giftTypesFilter.changeFilter}
                overrideClasses={{ checkbox: classes.checkbox }}
              />
            )}
            <AutocompleteMultiSelectFilter
              name={CATEGORIES_FILTER.key}
              label={CATEGORIES_FILTER.label}
              value={filters[CATEGORIES_FILTER.key]}
              options={categoriesFilter.options}
              isLoading={categoriesFilter.isLoading}
              onOpen={categoriesFilter.openFilter}
              onChange={categoriesFilter.changeFilter}
              overrideClasses={{ checkbox: classes.checkbox }}
            />
            <AutocompleteMultiSelectFilter
              name={BRANDS_FILTER.key}
              label={BRANDS_FILTER.label}
              value={filters[BRANDS_FILTER.key]}
              options={brandsFilter.options}
              isLoading={brandsFilter.isLoading}
              onOpen={brandsFilter.openFilter}
              onChange={brandsFilter.changeFilter}
              overrideClasses={{ checkbox: classes.checkbox }}
            />
          </Box>
        </Box>
        <Box width={160} sx={{ display: { lg: 'flex', sm: 'none', xs: 'none' } }}>
          <SortingSelect items={sortingItems} value={sorting.id} onSort={handleSort} />
        </Box>
      </Box>
      {!!filterChips.length && (
        <Box
          className={classes.chipsContainer}
          width={1}
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
          flexWrap="wrap"
        >
          <FilterChips
            onClearAll={handleClearAllChips}
            overrideClasses={{
              chipRoot: classes.chipRoot,
              chipDeleteIcon: classes.chipDeleteIcon,
              linkButton: classes.chipLinkButton,
            }}
          >
            {chips}
          </FilterChips>
        </Box>
      )}
      <Box sx={{ display: { lg: 'none', sm: 'flex', xs: 'flex' } }}>
        <Sidebar isOpen={isFiltersSidebarOpen} onClose={handleCloseFilters} width={400} header={sidebarHeader}>
          <MobileFilters
            isDonation={isDonation}
            isDeclaredDataEnabled={isDeclaredDataEnabled}
            hasCustomMarketplace={hasCustomMarketplace}
            isOpen={isFiltersSidebarOpen}
            search={search}
            isSearchEnabled={isSearchEnabled}
            sort={sorting}
            filters={filters}
            giftTypesFilter={giftTypesFilter}
            categoriesFilter={categoriesFilter}
            brandsFilter={brandsFilter}
            filterChips={filterChips}
            onApply={handleApplyFilters}
            isWhiteLabeling={isWhiteLabeling}
          />
        </Sidebar>
      </Box>
    </Box>
  );
};

MarketplaceFilters.propTypes = {
  isDonation: PropTypes.bool,
  isDeclaredDataEnabled: PropTypes.bool,
  hasCustomMarketplace: PropTypes.bool,
  countries: PropTypes.arrayOf(CountryShape),
  onSticky: PropTypes.func,
  onChangeCountry: PropTypes.func,
};

MarketplaceFilters.defaultProps = {
  isDonation: false,
  isDeclaredDataEnabled: false,
  hasCustomMarketplace: false,
  countries: undefined,
  onSticky: () => {},
  onChangeCountry: undefined,
};

export default memo(MarketplaceFilters);
